/**
 * Validation Results Component
 * 
 * Displays validation errors and warnings with detailed reporting
 * Allows users to download error reports and proceed with valid data
 */

import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Download, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { generateErrorReport } from './utils/validator';

const ValidationResults = ({ 
  validationResult, 
  onProceed, 
  onBack, 
  disabled = false 
}) => {
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [showWarningDetails, setShowWarningDetails] = useState(false);

  const handleDownloadErrorReport = () => {
    const csvContent = generateErrorReport(validationResult);
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `validation-errors-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getValidationIcon = () => {
    if (validationResult.errors.length === 0) {
      return <CheckCircle className="h-8 w-8 text-green-500" />;
    }
    return <AlertCircle className="h-8 w-8 text-red-500" />;
  };

  const getValidationStatus = () => {
    if (validationResult.errors.length === 0) {
      return {
        title: 'Validation Successful',
        message: 'All data has been validated and is ready for import.',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200'
      };
    }
    return {
      title: 'Validation Issues Found',
      message: 'Some data issues were found that must be resolved before import.',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200'
    };
  };

  const status = getValidationStatus();

  const ErrorTable = ({ errors, title, type = 'error' }) => {
    const iconColor = type === 'error' ? 'text-red-500' : 'text-yellow-500';
    const Icon = type === 'error' ? AlertCircle : AlertTriangle;
    
    return (
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-900 flex items-center space-x-2">
          <Icon className={`h-4 w-4 ${iconColor}`} />
          <span>{title} ({errors.length})</span>
        </h4>
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Row</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Field</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Issue</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {errors.slice(0, 10).map((error, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm text-gray-900">{error.row}</td>
                  <td className="px-4 py-2 text-sm text-gray-900">{error.field}</td>
                  <td className="px-4 py-2 text-sm text-gray-900">{error.message}</td>
                  <td className="px-4 py-2 text-sm text-gray-500 max-w-xs truncate" title={error.value}>
                    {error.value || <span className="italic">empty</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {errors.length > 10 && (
          <p className="text-sm text-gray-600">
            Showing first 10 of {errors.length} {type}s. Download the full report to see all issues.
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Validation Summary */}
      <div className={`border rounded-lg p-6 ${status.bgColor} ${status.borderColor}`}>
        <div className="flex items-start space-x-4">
          {getValidationIcon()}
          <div className="flex-1">
            <h3 className={`text-lg font-medium ${status.color}`}>{status.title}</h3>
            <p className={`text-sm mt-1 ${status.color}`}>{status.message}</p>
            
            <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-white rounded border">
                <div className="text-2xl font-bold text-blue-600">{validationResult.totalRows}</div>
                <div className="text-sm text-gray-600">Total Rows</div>
              </div>
              <div className="text-center p-3 bg-white rounded border">
                <div className="text-2xl font-bold text-green-600">{validationResult.validRows}</div>
                <div className="text-sm text-gray-600">Valid Rows</div>
              </div>
              <div className="text-center p-3 bg-white rounded border">
                <div className="text-2xl font-bold text-red-600">{validationResult.errors.length}</div>
                <div className="text-sm text-gray-600">Errors</div>
              </div>
              <div className="text-center p-3 bg-white rounded border">
                <div className="text-2xl font-bold text-yellow-600">{validationResult.warnings.length}</div>
                <div className="text-sm text-gray-600">Warnings</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Details */}
      {validationResult.errors.length > 0 && (
        <div className="bg-white border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <span>Validation Errors</span>
            </h3>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleDownloadErrorReport}
                className="flex items-center space-x-2 px-3 py-2 text-sm text-blue-600 hover:text-blue-800"
              >
                <Download className="h-4 w-4" />
                <span>Download Report</span>
              </button>
              <button
                onClick={() => setShowErrorDetails(!showErrorDetails)}
                className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                {showErrorDetails ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                <span>{showErrorDetails ? 'Hide' : 'Show'} Details</span>
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {/* Error Summary by Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(validationResult.errorSummary).map(([type, errors]) => {
                if (errors.length === 0) return null;
                return (
                  <div key={type} className="p-3 bg-red-50 rounded border border-red-200">
                    <div className="text-lg font-bold text-red-600">{errors.length}</div>
                    <div className="text-sm text-red-700 capitalize">
                      {type.replace('_', ' ')} Errors
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Detailed Error List */}
            {showErrorDetails && (
              <ErrorTable errors={validationResult.errors} title="All Errors" type="error" />
            )}
          </div>
        </div>
      )}

      {/* Warning Details */}
      {validationResult.warnings.length > 0 && (
        <div className="bg-white border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              <span>Validation Warnings</span>
            </h3>
            <button
              onClick={() => setShowWarningDetails(!showWarningDetails)}
              className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              {showWarningDetails ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              <span>{showWarningDetails ? 'Hide' : 'Show'} Details</span>
            </button>
          </div>

          <div className="mb-4 p-3 bg-yellow-50 rounded border border-yellow-200">
            <p className="text-sm text-yellow-700">
              Warnings indicate potential issues but won't prevent import. Review these items for data quality.
            </p>
          </div>

          {showWarningDetails && (
            <ErrorTable errors={validationResult.warnings} title="All Warnings" type="warning" />
          )}
        </div>
      )}

      {/* Valid Data Preview */}
      {validationResult.validRows > 0 && (
        <div className="bg-white border rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <span>Ready for Import</span>
          </h3>
          <div className="p-4 bg-green-50 rounded border border-green-200">
            <p className="text-sm text-green-700">
              <strong>{validationResult.validRows}</strong> rows have passed validation and are ready for import.
              {validationResult.errors.length > 0 && (
                <span> Invalid rows will be skipped during import.</span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          disabled={disabled}
          className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Back
        </button>
        
        <div className="flex space-x-3">
          {validationResult.errors.length > 0 && (
            <button
              onClick={handleDownloadErrorReport}
              className="px-6 py-3 border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 focus:ring-2 focus:ring-blue-500"
            >
              Download Error Report
            </button>
          )}
          <button
            onClick={onProceed}
            disabled={disabled || validationResult.validRows === 0}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {validationResult.errors.length > 0 ? 'Import Valid Rows' : 'Proceed with Import'}
          </button>
        </div>
      </div>
      
      {validationResult.validRows === 0 && (
        <p className="text-sm text-red-600 text-center">
          Cannot proceed with import. All rows have validation errors that must be fixed.
        </p>
      )}
    </div>
  );
};

export default ValidationResults; 